import { useEffect, useState, useCallback } from 'react';
import { format, addWeeks, subWeeks, eachDayOfInterval, isSameDay } from 'date-fns';
import { nl } from 'date-fns/locale';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { t } from '../../i18n/nl.js';
import { toAmsterdamTime, toAmsterdamDate, weekBounds } from '../../utils/dateUtils.js';
import { lesCoachNaam } from '../../utils/lesCoachUtils.js';

function DescriptionTooltip({ description }) {
  const [visible, setVisible] = useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-block', marginLeft: 4, verticalAlign: 'middle' }}>
      <span
        style={{ cursor: 'pointer', fontSize: 12, color: 'var(--color-text-muted)', userSelect: 'none' }}
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        onClick={() => setVisible(v => !v)}
        title={description}
      >
        ℹ
      </span>
      {visible && (
        <div style={{
          position: 'absolute',
          bottom: '120%',
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#1a1a2e',
          color: '#fff',
          padding: '6px 10px',
          borderRadius: 6,
          fontSize: 11,
          minWidth: 220,
          maxWidth: 320,
          zIndex: 200,
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          whiteSpace: 'normal',
          lineHeight: 1.4,
          pointerEvents: 'none',
        }}>
          {description}
        </div>
      )}
    </span>
  );
}

export default function ClientRoosterPage() {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [weekBase, setWeekBase] = useState(new Date());
  const [lessons, setLessons] = useState([]);
  const [myEnrollments, setMyEnrollments] = useState({});
  const [enrollmentCounts, setEnrollmentCounts] = useState({});
  const [clientCredits, setClientCredits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [pendingEnrollments, setPendingEnrollments] = useState([]);
  const [pendingActionId, setPendingActionId] = useState(null);
  const [pendingError, setPendingError] = useState('');
  const [isBlocked, setIsBlocked] = useState(false);

  const { start, end } = weekBounds(weekBase);

  const loadPendingEnrollments = useCallback(async () => {
    if (!user?.id) return;
    const now = new Date().toISOString();
    setPendingError('');
    try {
      const { data, error: err } = await supabase
        .from('lesson_enrollments')
        .select(`
          id, lesson_id,
          group_lessons!inner(id, name, starts_at, duration_minutes,
            locations(name),
            coach:coach_basic_profiles!group_lessons_coach_id_fkey(full_name),
            executing_coach:coach_basic_profiles!group_lessons_executing_coach_id_fkey(full_name))
        `)
        .eq('client_id', user.id)
        .eq('status', 'pending_confirmation')
        .gt('group_lessons.starts_at', now)
        .order('group_lessons(starts_at)', { ascending: true });
      if (err) throw err;
      setPendingEnrollments(data || []);
    } catch (err) {
      // Zelfde fix als in de app (TrainingenScreen, 2026-07-23):
      // een stille catch verborg dat de cliënt zijn openstaande uitnodigingen
      // niet zag — de sectie rendert alleen bij lengte > 0, dus een mislukte
      // load was niet van "geen uitnodigingen" te onderscheiden.
      console.error('Fout bij laden openstaande uitnodigingen:', err);
      setPendingError(t('clientRoosterUitnodigingen.foutLaden') + err.message);
    }
  }, [user?.id]);

  async function handleConfirm(enrollmentId) {
    setPendingActionId(enrollmentId);
    try {
      const { error: err } = await supabase.rpc('confirm_enrollment', {
        p_enrollment_id: enrollmentId,
      });
      if (err) throw err;
      showSuccess(t('clientRoosterUitnodigingen.bevestigdSucces'));
      await Promise.all([loadPendingEnrollments(), loadLessons()]);
    } catch (err) {
      showError(t('clientRoosterUitnodigingen.foutBevestigen') + err.message);
    } finally {
      setPendingActionId(null);
    }
  }

  async function handleDecline(enrollmentId) {
    setPendingActionId(enrollmentId);
    try {
      const { error: err } = await supabase.rpc('decline_enrollment', {
        p_enrollment_id: enrollmentId,
      });
      if (err) throw err;
      showSuccess(t('clientRoosterUitnodigingen.afgewezenSucces'));
      await Promise.all([loadPendingEnrollments(), loadLessons()]);
    } catch (err) {
      showError(t('clientRoosterUitnodigingen.foutAfwijzen') + err.message);
    } finally {
      setPendingActionId(null);
    }
  }

  const loadLessons = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const startISO = start.toISOString();
      const endISO = new Date(end.getTime() + 24 * 3600 * 1000).toISOString();
      const today = new Date().toISOString().slice(0, 10);

      const [lessonsRes, creditsRes] = await Promise.all([
        supabase
          .from('group_lessons')
          .select(`
            id, name, description, starts_at, duration_minutes,
            max_participants, credit_cost, is_cancelled,
            locations(name, is_active),
            coach:coach_basic_profiles!group_lessons_coach_id_fkey(full_name),
            executing_coach:coach_basic_profiles!group_lessons_executing_coach_id_fkey(full_name)
          `)
          .gte('starts_at', startISO)
          .lt('starts_at', endISO)
          .eq('is_cancelled', false)
          .order('starts_at', { ascending: true }),
        supabase
          .from('client_memberships')
          .select('current_credits')
          .eq('client_id', user.id)
          .or(`end_date.is.null,end_date.gte.${today}`)
          .order('created_at', { ascending: false })
          .order('id', { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);

      if (lessonsRes.error) throw lessonsRes.error;

      const data = (lessonsRes.data || []).filter(l =>
        !l.locations || l.locations.is_active !== false
      );

      setLessons(data);
      setClientCredits(creditsRes.data?.current_credits ?? null);

      if (data.length > 0) {
        const ids = data.map(l => l.id);

        const [enrollRes, myRes] = await Promise.all([
          supabase
            .from('lesson_enrollments')
            .select('lesson_id')
            .in('lesson_id', ids)
            .eq('status', 'enrolled'),
          supabase
            .from('lesson_enrollments')
            .select('lesson_id, status')
            .in('lesson_id', ids)
            .in('status', ['enrolled', 'waitlisted'])
            .eq('client_id', user.id),
        ]);

        if (enrollRes.error) throw enrollRes.error;
        if (myRes.error) throw myRes.error;

        const counts = {};
        (enrollRes.data || []).forEach(e => {
          counts[e.lesson_id] = (counts[e.lesson_id] || 0) + 1;
        });
        setEnrollmentCounts(counts);

        const myMap = {};
        (myRes.data || []).forEach(e => { myMap[e.lesson_id] = e.status; });
        setMyEnrollments(myMap);
      } else {
        setEnrollmentCounts({});
        setMyEnrollments({});
      }
    } catch (err) {
      setError(t('clientRooster.foutLaden') + err.message);
    } finally {
      setLoading(false);
    }
  }, [start.toISOString(), end.toISOString()]);

  useEffect(() => {
    loadLessons();
    loadPendingEnrollments();
    // Check blokkering
    if (user?.id) {
      supabase.from('profiles').select('blocked_at').eq('id', user.id).maybeSingle()
        .then(({ data }) => setIsBlocked(!!data?.blocked_at));
    }
  }, [loadLessons, loadPendingEnrollments]);

  async function handleEnroll(lessonId) {
    setActionLoading(lessonId);
    try {
      const { data, error: rpcErr } = await supabase.rpc('enroll_client', { p_lesson_id: lessonId });
      if (rpcErr) throw rpcErr;

      if (data?.error) {
        const errMap = {
          les_niet_gevonden: t('clientRooster.foutLesBestaat'),
          geen_lidmaatschap: t('clientRooster.foutGeenLidmaatschap'),
          onvoldoende_credits: t('clientRooster.foutOnvoldoendeCredits'),
          abonnement_niet_toegestaan: t('clientRooster.foutAbonnementNietToegestaan'),
        };
        showError(errMap[data.error] || t('clientRooster.foutInschrijven') + data.error);
      } else if (data?.status === 'waitlisted') {
        showSuccess(t('clientRooster.wachtlijstGeplaatst'));
        await loadLessons();
      } else {
        showSuccess(t('clientRooster.ingeschrevenSucces'));
        await loadLessons();
      }
    } catch (err) {
      const msg = err.message || '';
      if (msg.includes('Account geblokkeerd')) {
        showError(t('clientRooster.foutAccountGeblokkeerd'));
      } else if (msg.includes('verleden')) {
        showError(t('clientRooster.foutLesVerleden'));
      } else {
        showError(t('clientRooster.foutInschrijven') + msg);
      }
    } finally {
      setActionLoading(null);
    }
  }

  async function handleUnenroll(lessonId) {
    setActionLoading(lessonId);
    try {
      const { data, error: rpcErr } = await supabase.rpc('unenroll_client', { p_lesson_id: lessonId });
      if (rpcErr) throw rpcErr;

      if (data?.error) {
        const errMap = {
          niet_ingeschreven: t('clientRooster.foutNietIngeschreven'),
          deadline_verstreken: t('clientRooster.foutDeadlineVerstreken'),
        };
        showError(errMap[data.error] || t('clientRooster.foutUitschrijven') + data.error);
      } else {
        showSuccess(t('clientRooster.uitgeschrevenSucces'));
        await loadLessons();
      }
    } catch (err) {
      showError(t('clientRooster.foutUitschrijven') + err.message);
    } finally {
      setActionLoading(null);
    }
  }

  const days = eachDayOfInterval({ start, end });
  const now = new Date();

  function lessonsForDay(day) {
    return lessons.filter(l => isSameDay(toAmsterdamDate(l.starts_at), day));
  }

  return (
    <div>
      {isBlocked && (
        <div style={{ background: '#dc262622', borderRadius: 8, padding: 12, marginBottom: 16 }}>
          <p style={{ color: '#dc2626', fontWeight: 600, margin: 0, textAlign: 'center' }}>
            {t('clientRooster.accountGeblokkeerd')}
          </p>
        </div>
      )}
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('clientRooster.title')}</h1>
          <p className="page-subtitle">
            {t('clientRooster.weekVan')} {format(start, 'd MMM', { locale: nl })} {t('clientRooster.tm')} {format(end, 'd MMM yyyy', { locale: nl })}
            {clientCredits !== null && (
              <span style={{ marginLeft: 16, color: 'var(--color-primary)', fontWeight: 600 }}>
                {clientCredits} {t('clientRooster.creditsResterend')}
              </span>
            )}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" onClick={() => setWeekBase(w => subWeeks(w, 1))}>
            {t('clientRooster.vorigeWeek')}
          </button>
          <button className="btn btn-ghost" onClick={() => setWeekBase(new Date())}>
            {t('clientRooster.vandaag')}
          </button>
          <button className="btn btn-ghost" onClick={() => setWeekBase(w => addWeeks(w, 1))}>
            {t('clientRooster.volgendeWeek')}
          </button>
        </div>
      </div>

      {error && <div className="error-msg" style={{ marginBottom: 20 }}>{error}</div>}

      {/* Openstaande uitnodigingen */}
      {pendingError && (
        <div className="error-msg" style={{ marginBottom: 20 }}>{pendingError}</div>
      )}
      {pendingEnrollments.length > 0 && (
        <div style={{
          border: '2px solid #d97706', borderRadius: 'var(--radius)',
          padding: 20, marginBottom: 24,
          background: 'rgba(217,119,6,0.04)',
        }}>
          <div style={{
            fontSize: 13, fontWeight: 700, color: '#d97706',
            textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 14,
          }}>
            {t('clientRoosterUitnodigingen.titel')} ({pendingEnrollments.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {pendingEnrollments.map(enrollment => {
              const les = enrollment.group_lessons;
              const datum = format(toAmsterdamDate(les.starts_at), 'EEEE d MMMM yyyy', { locale: nl });
              const tijd = toAmsterdamTime(les.starts_at);
              const isBusy = pendingActionId === enrollment.id;
              return (
                <div key={enrollment.id} style={{
                  padding: '12px 16px',
                  background: 'var(--color-surface)',
                  borderRadius: 8,
                  border: '1px solid var(--color-border)',
                  display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--color-text)', marginBottom: 2 }}>
                      {les.name}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                      {datum} · {tijd} · {les.duration_minutes} min
                    </div>
                    {les.locations?.name && (
                      <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 2 }}>
                        📍 {les.locations.name}
                      </div>
                    )}
                    {!!lesCoachNaam(les) && (
                      <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 2 }}>
                        👤 {lesCoachNaam(les)}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => handleConfirm(enrollment.id)}
                      disabled={isBusy}
                      style={{ minWidth: 100 }}
                    >
                      {isBusy ? '…' : t('clientRoosterUitnodigingen.bevestigen')}
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => handleDecline(enrollment.id)}
                      disabled={isBusy}
                      style={{ minWidth: 80, color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}
                    >
                      {isBusy ? '…' : t('clientRoosterUitnodigingen.afwijzen')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : (
        <div className="table-wrap">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(130px, 1fr))', gap: 12 }}>
          {days.map(day => {
            const dayLessons = lessonsForDay(day);
            const isToday = isSameDay(day, now);
            return (
              <div key={day.toISOString()}>
                <div style={{
                  padding: '8px 12px',
                  marginBottom: 8,
                  borderRadius: 'var(--radius)',
                  background: isToday ? 'var(--color-primary)' : 'transparent',
                  color: isToday ? '#fff' : 'var(--color-text-muted)',
                  fontWeight: isToday ? 700 : 500,
                  fontSize: 12,
                  textAlign: 'center',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}>
                  {format(day, 'EEE d', { locale: nl })}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {dayLessons.length === 0 ? (
                    <div style={{
                      padding: '16px 12px',
                      background: 'var(--color-bg)',
                      borderRadius: 'var(--radius)',
                      textAlign: 'center',
                      fontSize: 12,
                      color: 'var(--color-text-muted)',
                    }}>
                      —
                    </div>
                  ) : (
                    dayLessons.map(les => {
                      const enrolled = enrollmentCounts[les.id] || 0;
                      const isFull = enrolled >= les.max_participants;
                      const myStatus = myEnrollments[les.id];
                      const isEnrolled = myStatus === 'enrolled';
                      const isWaitlisted = myStatus === 'waitlisted';
                      const busy = actionLoading === les.id;
                      const isVerlopen = new Date(les.starts_at) < now;

                      return (
                        <div
                          key={les.id}
                          className="card"
                          style={{
                            padding: '12px',
                            borderLeft: `3px solid ${isEnrolled ? 'var(--color-primary)' : isWaitlisted ? '#f59e0b' : isVerlopen ? 'var(--color-border)' : 'var(--color-border)'}`,
                            opacity: isVerlopen ? 0.7 : 1,
                          }}
                        >
                          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 0 }}>
                            <span>{les.name}</span>
                            {les.description && <DescriptionTooltip description={les.description} />}
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4 }}>
                            {toAmsterdamTime(les.starts_at)} · {les.duration_minutes} {t('clientRooster.min')}
                          </div>
                          {!!lesCoachNaam(les) && (
                            <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 4 }}>
                              👤 {lesCoachNaam(les)}
                            </div>
                          )}
                          {les.locations?.name && (
                            <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 6 }}>
                              📍 {les.locations.name}
                            </div>
                          )}
                          <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 8 }}>
                            {enrolled}/{les.max_participants} {t('clientRooster.plaatsen')}
                          </div>

                          {isVerlopen ? (
                            <div style={{ fontSize: 11, fontWeight: 600, color: isEnrolled ? 'var(--color-primary)' : 'var(--color-text-muted)' }}>
                              {isEnrolled ? `✓ ${t('clientRooster.gevolgd')}` : t('clientRooster.verlopen')}
                            </div>
                          ) : (
                            <>
                              {isEnrolled && (
                                <div style={{ marginBottom: 6 }}>
                                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-primary)', display: 'block', marginBottom: 4 }}>
                                    ✓ {t('clientRooster.ingeschreven')}
                                  </span>
                                  <button
                                    className="btn btn-ghost btn-sm"
                                    style={{ width: '100%', fontSize: 11 }}
                                    onClick={() => handleUnenroll(les.id)}
                                    disabled={busy}
                                  >
                                    {busy ? '…' : t('clientRooster.uitschrijven')}
                                  </button>
                                </div>
                              )}

                              {isWaitlisted && (
                                <div style={{ marginBottom: 6 }}>
                                  <span style={{ fontSize: 11, fontWeight: 600, color: '#f59e0b', display: 'block', marginBottom: 4 }}>
                                    ⏳ {t('clientRooster.opWachtlijst')}
                                  </span>
                                  <button
                                    className="btn btn-ghost btn-sm"
                                    style={{ width: '100%', fontSize: 11 }}
                                    onClick={() => handleUnenroll(les.id)}
                                    disabled={busy}
                                  >
                                    {busy ? '…' : t('clientRooster.uitschrijven')}
                                  </button>
                                </div>
                              )}

                              {!myStatus && !isBlocked && (
                                isFull ? (
                                  <button
                                    className="btn btn-secondary btn-sm"
                                    style={{ width: '100%', fontSize: 11 }}
                                    onClick={() => handleEnroll(les.id)}
                                    disabled={busy}
                                  >
                                    {busy ? '…' : t('clientRooster.opWachtlijst')}
                                  </button>
                                ) : (
                                  <button
                                    className="btn btn-primary btn-sm"
                                    style={{ width: '100%', fontSize: 11 }}
                                    onClick={() => handleEnroll(les.id)}
                                    disabled={busy}
                                  >
                                    {busy ? '…' : t('clientRooster.inschrijven')}
                                  </button>
                                )
                              )}
                            </>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
        </div>
      )}
    </div>
  );
}
